import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
// import styles from "./styles/style.module.css"
import styles from "../styles/style.module.css"
import PhoneInput, { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';


interface Country {
  _id: string;
  name: string;
  iso: string;
}

interface FreeDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FreeDataModal: React.FC<FreeDataModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
    handsetModelNo: ''
  });
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showHandsetModal, setShowHandsetModal] = useState<boolean>(false);

  // Fetch countries on component mount
  useEffect(() => {
    if (isOpen) {
      fetchCountries();
    }
  }, [isOpen]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api-cb.commbitz.com/regions/countries?onlyCountries=true');
      const data = await response.json();
      
      if (data.statusCode === 200 && data.data?.countries) {
        setCountries(data.data.countries);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.phoneNumber || !formData.handsetModelNo) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Phone number validation
    if (!isPossiblePhoneNumber(formData.phoneNumber) || !isValidPhoneNumber(formData.phoneNumber)) {
      toast.error('Invalid phone number');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        countryCode: formData.countryCode,
        phoneNumber: formData.phoneNumber,
        travellingCountry: "65d33654136c54ab64b20a5b", // Singapore ID
        handsetModelNo: formData.handsetModelNo,
        partnerWebsiteId: "6835681d682191fdd0713d41"
      };

      const response = await fetch('https://api-cb.commbitz.com/user-partner-info/travelx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Free data request submitted successfully!');
        onClose();
        onSuccess();
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          countryCode: '',
          handsetModelNo: ''
        });
      } else {
        toast.error(result.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
        <div className={styles.bassoDtailFoem}>
          <div className="modal-header">
            <h5 className="modal-title">
              <span className="me-2">🎁</span>
              Redeem your free 1GB
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          
          <div className="modal-body p-0 rounded-0">
          <div className={styles.bassoDtailFoemdetail}>
            <p className="text-muted mb-4">
              Stay connected the moment you land in Singapore. Instant activation, no SIM card hassle, data ready on your phone.
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className={styles.formGroup}>
                    <label>First Name*</label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Enter First Name"
                      onChange={handleInputChange}
                      value={formData.firstName}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={styles.formGroup}>
                    <label>Last Name*</label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Enter Last Name"
                      onChange={handleInputChange}
                      value={formData.lastName}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={styles.formGroup}>
                    <label>Email*</label>
                    <input
                      type="text"
                      name="email"
                      placeholder="Enter your email here"
                      onChange={handleInputChange}
                      value={formData.email}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={styles.formGroup}>
                    <label>Phone*</label>
                    <PhoneInput
                      placeholder="Enter your phone number here"
                      onChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          phoneNumber: value || ''
                        }));
                      }}
                      value={formData.phoneNumber}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={styles.formGroup}>
                    <div className='d-flex align-items-center justify-content-between'>
                      <div className='col-6'> <label>Handset Model No*</label></div>
                      <div className='col-6 text-end'>
                        <p 
                          className='px-lg-2 px-1 bg-black text-white d-inline-block rounded-2 mt-0 mb-0'
                          onClick={() => setShowHandsetModal(!showHandsetModal)}
                          style={{ cursor: 'pointer' }}
                        >
                          Where i could find this?
                        </p>
                      </div>
                    </div>
                    <input
                      type="text"
                      name="handsetModelNo"
                      placeholder="Enter handset model number"
                      onChange={handleInputChange}
                      value={formData.handsetModelNo}
                    />
                    
                    {/* Handset Model Dropdown */}
                    {showHandsetModal && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                          <strong style={{ color: '#333' }}>Choose Handset Model:</strong>
                        </div>
                        {[
                          'iPhone 15 Pro Max',
                          'iPhone 15 Pro',
                          'iPhone 15 Plus',
                          'iPhone 15',
                          'iPhone 14 Pro Max',
                          'iPhone 14 Pro',
                          'iPhone 14 Plus',
                          'iPhone 14',
                          'iPhone 13 Pro Max',
                          'iPhone 13 Pro',
                          'iPhone 13',
                          'iPhone 12 Pro Max',
                          'iPhone 12 Pro',
                          'iPhone 12',
                          'iPhone 11 Pro Max',
                          'iPhone 11 Pro',
                          'iPhone 11',
                          'iPhone SE (3rd generation)',
                          'iPhone SE (2nd generation)'
                        ].map((model, index) => (
                          <div
                            key={index}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, handsetModelNo: model }));
                              setShowHandsetModal(false);
                            }}
                            style={{
                              padding: '8px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => {
                              (e.target as HTMLElement).style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              (e.target as HTMLElement).style.backgroundColor = 'white';
                            }}
                          >
                            {model}
                          </div>
                        ))}
                        
                        <div style={{ padding: '10px', borderBottom: '1px solid #eee', borderTop: '1px solid #eee' }}>
                          <strong style={{ color: '#333' }}>Popular Samsung Models:</strong>
                        </div>
                        {[
                          'Samsung Galaxy S24 Ultra',
                          'Samsung Galaxy S24+',
                          'Samsung Galaxy S24',
                          'Samsung Galaxy S23 Ultra',
                          'Samsung Galaxy S23+',
                          'Samsung Galaxy S23',
                          'Samsung Galaxy S22 Ultra',
                          'Samsung Galaxy S22+',
                          'Samsung Galaxy S22',
                          'Samsung Galaxy Z Fold5',
                          'Samsung Galaxy Z Flip5',
                          'Samsung Galaxy A54 5G',
                          'Samsung Galaxy A34 5G'
                        ].map((model, index) => (
                          <div
                            key={`samsung-${index}`}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, handsetModelNo: model }));
                              setShowHandsetModal(false);
                            }}
                            style={{
                              padding: '8px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => {
                              (e.target as HTMLElement).style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              (e.target as HTMLElement).style.backgroundColor = 'white';
                            }}
                          >
                            {model}
                          </div>
                        ))}
                        
                        <div style={{ padding: '10px', borderBottom: '1px solid #eee', borderTop: '1px solid #eee' }}>
                          <strong style={{ color: '#333' }}>Other Popular Models:</strong>
                        </div>
                        {[
                          'Google Pixel 8 Pro',
                          'Google Pixel 8',
                          'Google Pixel 7 Pro',
                          'Google Pixel 7',
                          'OnePlus 11',
                          'OnePlus 10 Pro',
                          'Xiaomi 13 Ultra',
                          'Xiaomi 13 Pro',
                          'Xiaomi 13',
                          'OPPO Find X6 Pro',
                          'OPPO Find X6',
                          'Nothing Phone (2)',
                          'Nothing Phone (1)'
                        ].map((model, index) => (
                          <div
                            key={`other-${index}`}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, handsetModelNo: model }));
                              setShowHandsetModal(false);
                            }}
                            style={{
                              padding: '8px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => {
                              (e.target as HTMLElement).style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              (e.target as HTMLElement).style.backgroundColor = 'white';
                            }}
                          >
                            {model}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="col-md-12">
                <div className={styles.formGroup}>
                  <input type="submit" value="Submit" />
                  <h6 className='text-lg-start text-center mt-2'>
                    <i className='pe-2'>🛡️</i>
                    We value your privacy - your details are safe with us.
                  </h6>
                </div>
              </div>
            </form>
          </div>
        </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default FreeDataModal;
